import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Standalone three-dot card menu matching the Workflow Labs design system.
 * Menu is portaled to document.body so it is not clipped by card overflow.
 */
export default function CardMenu({ items = [], className }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const visibleItems = items.filter(Boolean);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 176; // w-44
      const gap = 4;
      let left = rect.right - menuWidth;
      let top = rect.bottom + gap;

      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - menuWidth - 8);
      }

      // Flip upward if near bottom of viewport
      const estimatedHeight = visibleItems.length * 36 + 8;
      if (top + estimatedHeight > window.innerHeight - 8 && rect.top > estimatedHeight) {
        top = rect.top - estimatedHeight - gap;
      }

      setCoords({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, visibleItems.length]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = e => {
      if (
        triggerRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (visibleItems.length === 0) return null;

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
        className="p-1 rounded-md text-mute hover:text-ink hover:bg-white/5 transition-colors"
        aria-label="Card actions"
        aria-expanded={open}>
        <MoreVertical className="w-4 h-4" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: coords.top, left: coords.left }}
            className="fixed z-[100] w-44 py-1 rounded-[12px] bg-paper-2 border border-shell-border shadow-soft overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {visibleItems.map(({ label, icon: Icon, onClick, danger }) => (
              <button
                key={label}
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setOpen(false);
                  onClick?.();
                }}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                  danger ? 'text-[#EF4444] hover:bg-[#EF4444]/10' : 'text-mute hover:bg-white/5 hover:text-ink',
                )}>
                {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                {label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
